import * as React from "react";
import {
    Box,
    Button,
    Flashbar,
    Link,
    Modal,
    Popover,
    ProgressBar,
    Select,
    SpaceBetween,
    TextContent,
    Toggle
} from "@amzn/awsui-components-react";
import { AppContext } from "../../../setup/context";
import { EntitySelection } from "../../common/EntitySelection";
import {
    APIOutput,
    AppError,
    ContainerRef,
    EntityRef,
    IContainer,
    Instance,
    IPage,
    InstanceFactory,
    PageFactory,
    PageLibrary,
} from "@amzn/ask-legal-domain";
import { UIField } from "../../common/UIField";
import { ErrorFlashbar } from "../../common/ErrorFlashbar";
import { useNavigate } from "react-router-dom";
import { PageLibraryLink } from "../../pageLibrary/PageLibraryLink";
import { UIModel } from "../../../model/ui-model";
import { PageModel } from "../../../model/page-model";

type CopyStatus = "New" | "Running" | "Succeeded" | "Error";

export const DeepCopyPageModal = (props: {
    onDismiss: () => void;
    pageId: string;
    anticipatedExecutionTime?: number;
    prefetchedInstances?: { label: string; value: string }[];
}) => {
    const navigate = useNavigate();
    const context = React.useContext(AppContext);
    const currentInstanceId = InstanceFactory.fromEntityId(props.pageId);

    const [selectedInstance, setSelectedInstance] = React.useState<EntityRef>({ id: currentInstanceId } as EntityRef);
    const [instanceOptions, setInstanceOptions] = React.useState(
        props.prefetchedInstances || [{ label: currentInstanceId, value: currentInstanceId }]
    );
    const [instancesLoaded, setInstancesLoaded] = React.useState(!!props.prefetchedInstances);

    React.useEffect(() => {
        if (props.prefetchedInstances) return;
        context.getInstanceAPI().loadAll().then(res => {
            const output = APIOutput.fromRaw<Instance[]>(res.data);
            if (output.isOk()) {
                setInstanceOptions(output.data.map(inst => ({
                    label: inst.name || inst.id,
                    value: inst.id
                })).sort((a, b) => a.label.localeCompare(b.label)));
            }
            setInstancesLoaded(true);
        }).catch(() => setInstancesLoaded(true));
    }, []);
    const [targetPageLibrary, setTargetPageLibrary] = React.useState<EntityRef>(null);
    const [deprecateSourcePage, setDeprecateSourcePage] = React.useState<boolean>(false);
    const [redirectionCountDown, setRedirectionCountDown] = React.useState<number>(5);
    const targetPageName = UIModel.State.useWithRegexAndCharLimit({
        initialValue: PageFactory.getNameFromPageId(props.pageId),
        regex: new RegExp(/^[a-zA-Z0-9]+$/),
        characterLimit: PageModel.TITLE_CHAR_LIMIT
    });

    const isCrossInstance = selectedInstance && selectedInstance.id !== currentInstanceId;

    // Multi-call copy state
    const [status, setStatus] = React.useState<CopyStatus>("New");
    const [progressText, setProgressText] = React.useState("");
    const [progressValue, setProgressValue] = React.useState(0);
    const [error, setError] = React.useState<AppError | null>(null);
    const [newPageId, setNewPageId] = React.useState<string | null>(null);
    const [partialWarning, setPartialWarning] = React.useState<string | null>(null);

    // Cancellation flag — prevents in-flight copy from continuing after modal dismiss
    const cancelledRef = React.useRef(false);
    React.useEffect(() => { return () => { cancelledRef.current = true; }; }, []);

    const triggerPageCopy = async () => {
        setStatus("Running");
        setProgressValue(0);
        setProgressText("Initializing page copy...");
        setError(null);

        try {
            // Step 1: Init — create page shell, get container list
            const initInput = IPage.DeepCopyPageInitInput.create({
                sourcePageId: props.pageId,
                targetPageId: PageFactory.toPageId(targetPageLibrary.id, targetPageName.value),
                deprecateSourcePage,
                ...(isCrossInstance && {
                    targetInstanceId: selectedInstance.id,
                    targetPageLibraryId: targetPageLibrary.id,
                    newPageName: targetPageName.value,
                }),
            });

            const initRes = await context.getPageAPI().deepCopyInit(initInput);
            if (cancelledRef.current) return;
            const initOutput = APIOutput.fromRaw<IPage.DeepCopyPageInitOutput>(initRes.data);
            if (initOutput.isErr()) {
                throw new Error(initOutput.err.message);
            }

            const { targetPageId, containers } = initOutput.data;
            setNewPageId(targetPageId);
            const totalContainers = containers.length;

            // Step 2: Copy each container individually
            const mainContainers: ContainerRef[] = [];
            const rightContainers: ContainerRef[] = [];
            const failedContainerTypes: string[] = [];

            for (let i = 0; i < totalContainers; i++) {
                if (cancelledRef.current) return;
                const container = containers[i];
                const pct = Math.round(((i + 1) / (totalContainers + 1)) * 90);
                setProgressText(`Copying container ${i + 1} of ${totalContainers}...`);
                setProgressValue(pct);

                const copyRes = await context.getContainerAPI().deepCopy({
                    containerId: container.containerId,
                    targetPageId,
                });
                const copyOutput = APIOutput.fromRaw<IContainer.DeepCopyContainerOutput>(copyRes.data);
                if (copyOutput.isErr()) {
                    // Skip unsupported container types (422)
                    if (copyOutput.err.code === 422) continue;
                    failedContainerTypes.push(container.containerType);
                    continue;
                }

                if (container.section === "Main") {
                    mainContainers.push(copyOutput.data.newContainer);
                } else {
                    rightContainers.push(copyOutput.data.newContainer);
                }
            }

            if (mainContainers.length === 0 && rightContainers.length === 0) {
                throw new Error("All containers failed to copy");
            }

            if (failedContainerTypes.length > 0) {
                setPartialWarning(`Some containers failed to copy: ${failedContainerTypes.join(", ")}`);
            }

            // Step 3: Finalize — assemble page, create permissions, copy labels, deprecate source
            setProgressText("Finalizing page copy...");
            setProgressValue(95);
            if (cancelledRef.current) return;

            const finalizeInput = IPage.DeepCopyPageFinalizeInput.create({
                targetPageId,
                sourcePageId: props.pageId,
                deprecateSourcePage,
                mainContainers,
                rightContainers,
            });

            const finalizeRes = await context.getPageAPI().deepCopyFinalize(finalizeInput);
            const finalizeOutput = APIOutput.fromRaw<IPage.DeepCopyPageFinalizeOutput>(finalizeRes.data);
            if (finalizeOutput.isErr()) {
                throw new Error(finalizeOutput.err.message);
            }

            setProgressValue(100);
            setProgressText("Copy complete!");
            setStatus("Succeeded");
        } catch (e: any) {
            const message = e?.response?.data?.message || e?.message || "Unknown error";
            setError(AppError.create(message, 500));
            setStatus("Error");
        }
    };

    // Reset page library when instance changes
    React.useEffect(() => {
        setTargetPageLibrary(null);
    }, [selectedInstance?.id]);

    // Redirect countdown after success
    React.useEffect(() => {
        if (status !== "Succeeded" || !newPageId) return;
        const countdown = setInterval(() => {
            setRedirectionCountDown(prev => prev > 0 ? prev - 1 : 0);
        }, 1000);
        const redirect = setTimeout(() => {
            navigate(`/page/${newPageId}/live`);
        }, 5000);
        return () => {
            clearInterval(countdown);
            clearTimeout(redirect);
        };
    }, [status]);

    const targetPageId = targetPageLibrary && targetPageName.value
        ? PageFactory.toPageId(targetPageLibrary.id, targetPageName.value)
        : null;

    return (
        <Modal
            visible={true}
            size="medium"
            header="Copy page"
            onDismiss={status === "Running" ? () => {} : props.onDismiss}
            footer={
                <Box float="right">
                    <Button variant="link"
                        disabled={status === "Running"}
                        onClick={props.onDismiss}
                    >
                        Cancel
                    </Button>
                    <Button variant="primary"
                        loading={status === "Running"}
                        disabled={
                            !selectedInstance ||
                            !targetPageLibrary ||
                            targetPageName.errorText ||
                            !targetPageName.value ||
                            (!isCrossInstance && props.pageId === PageFactory.toPageId(targetPageLibrary.id, targetPageName.value)) ||
                            status === "Succeeded"
                        }
                        onClick={triggerPageCopy}
                    >
                        Confirm
                    </Button>
                </Box>
            }>
            {status === "New" &&
                <SpaceBetween size="xs">
                    <UIField.CustomField
                        name="Select instance:"
                        child={<Select
                            selectedOption={instanceOptions.find(o => o.value === selectedInstance?.id) || null}
                            options={instanceOptions}
                            onChange={({ detail }) => setSelectedInstance({ id: detail.selectedOption.value } as EntityRef)}
                            disabled={status !== "New"}
                            statusType={instancesLoaded ? "finished" : "loading"}
                            loadingText="Loading instances..."
                            filteringType="auto"
                            placeholder="Select instance"
                        />}
                    />
                    {selectedInstance &&
                        <UIField.CustomField
                            name="Please select a page library to copy the page to:"
                            child={<EntitySelection.SingleWithPagination<PageLibrary>
                                key={selectedInstance.id}
                                api={context.getPageLibraryAPI().loadAll}
                                partitionKey={selectedInstance.id}
                                pageSize={20}
                                selected={targetPageLibrary}
                                onSelectionChange={e => setTargetPageLibrary(e)}
                            />}
                        />
                    }
                    <UIField.StateValueField
                        name="New Page Name (part of the URL)"
                        state={targetPageName}
                        constraintText={
                            <React.Fragment>
                                Maximum {PageModel.TITLE_CHAR_LIMIT} characters <Popover
                                    dismissAriaLabel="Close"
                                    header="Page Naming Guidelines"
                                    triggerType="custom"
                                    content={<React.Fragment>
                                        <small>
                                            <ul>
                                                <li>Cannot contain spaces</li>
                                                <li>Cannot contain any special characters</li>
                                                <li>Can contain alphabets <b>A...Z</b> <b>a...z</b></li>
                                                <li>Can contain numbers <b>0-9</b></li>
                                                <li><b><em>Examples : </em></b> SamplePageName, 1New2Document3, ... etc</li>
                                            </ul>
                                        </small>
                                    </React.Fragment>}
                                >
                                    <Link variant="info">Format</Link>
                                </Popover></React.Fragment>}
                        editing={true}
                    />
                    <Toggle
                        checked={deprecateSourcePage}
                        onChange={(e: { detail: { checked: boolean; }; }) => setDeprecateSourcePage(e.detail.checked)}
                    >
                        Deprecate current page
                    </Toggle>
                </SpaceBetween>}
            {targetPageLibrary &&
                <Box padding={{ top: "m" }}>
                    <TextContent>Destination Page Library: <PageLibraryLink external id={targetPageLibrary.id} /><br /></TextContent>
                </Box>}
            {targetPageId && !targetPageName.errorText &&
                <Box padding={{ top: "m", bottom: "m" }}>
                    <TextContent>Destination Page URL: <small>/page/{targetPageId}/live</small><br /></TextContent>
                </Box>
            }
            {(status === "Running" || status === "Succeeded") && <ProgressBar
                value={progressValue}
                additionalInfo={status === "Running" ? "Please do not refresh the page nor close the tab..." : undefined}
                description={progressText}
                label="Copying in progress"
            />}
            {status === "Succeeded" && <Flashbar items={[
                ...(partialWarning ? [{
                    type: "warning" as const,
                    content: partialWarning,
                    dismissible: false,
                    id: "partial_warning"
                }] : []),
                {
                    type: "success",
                    content: <div>Copying was successful, you will be redirected in {redirectionCountDown} seconds.</div>,
                    dismissible: false,
                    id: "message_1"
                }
            ]} />}
            {status === "Error" &&
                <ErrorFlashbar error={error} />
            }
        </Modal>
    );
};
